import _ from "lodash";
import React, { MutableRefObject, useContext, useEffect, useRef } from "react";
import postal from 'postal';
import { Portal } from 'react-portal';

import CheckBox from "../../../../../ui/CheckBox";
import CheckBoxUncontrolled from '../../../../../ui/CheckBoxUncontrolled';
import DangerButton from '../../../../../ui/DangerButton';
import GreenButton from "../../../../../ui/GreenButton";
import TextBox from "../../../../../ui/TextBox";
import InlineInputControlRow from "../../../../../ui/layout/InlineInputControlRow";
import Label from "../../../../../ui/Label";
import MultilineTextBox from "../../../../../ui/MultilineTextBox";
import PrimaryButton from '../../../../../ui/PrimaryButton';
import SecondaryButton from "../../../../../ui/SecondaryButton";
import Select from "../../../../../ui/Select";
import SmartTable from "../../../../../uiControls/SmartTable";
import { NumberedIndicatorRenderer, TextAlignLeftRenderer, FloatRenderer, TextRenderer, DateRenderer, NullableCheckboxIconRenderer, BlueTextRenderer } from "../../../../../uiControls/SmartTableRenderers";

import useGlobalOkDialog from "../../../../../hooks/useGlobalOkDialog";
import useReportViewer from '../../../../../hooks/useReportViewer';
import { ApplicableTableSettings } from "../../../../../tableSettings";
import { formatDate, now, getDate } from '../../../../../dateUtils';
import { batchStatementProcessingTable } from "../../../../reports/tableSettings";
import { useTypedFastStorage } from "../../../../../hooks/useFastStorage";

import ApplicationContext, {IApplicationContext} from "../../../../../ApplicationContext";
import { BatchStatementDetail } from "../../../../../fms.core/BatchStatementDetail";
import { BatchStatementHd } from "../../../../../fms.core/BatchStatementHd";
import { DocumentType } from "../../../../../framework.core/DocumentType";
import { JobNames } from "../../../../../framework.core/JobNames";
import { LocalStatementArgs } from '../../../../../fms.report/args';
import { focusTabableByIndex } from "../../../../../util/inputUtils";

interface BatchStatementStorageKeys {
  modelStorage: string,
};

interface BatchStatementViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  jobName: JobNames,
};

interface BatchStatementLoadedDetailsViewParams {
  batchStatement: BatchStatementHd,
  batchStatementRef: MutableRefObject<BatchStatementHd|null>,
  setBatchStatement: (x: Partial<BatchStatementHd>) => void,
  onPopupClose?: () => void,
  jobName: JobNames,
  tbId: string | undefined,
  appContext: IApplicationContext,
  loadList: () => void
};

const ProgressDialogStyle:React.CSSProperties = {
  position: 'fixed',
  inset: '0px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9999999,
  display: 'none',
};

const ProgressDialogContainerStyle:React.CSSProperties = {
  position: 'absolute',
  inset: '50% auto auto 50%',
  border: '1px solid rgb(204, 204, 204)',
  background: 'rgb(255, 255, 255)',
  overflow: 'auto',
  borderRadius: '4px',
  outline: 'none',
  padding: '20px',
  marginRight: '-50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 9999999,
};

const actualDate = ['90+ days', '60+ days', '45+ days', '30+ days', '15+ days', 'ALL',
'61 ~ 90 days', '46 ~ 60 days', '31 ~ 45 days', '16 ~ 30 days', '1 ~ 15 days'];

function getBatchStatementStorageKeys(jobName: JobNames): BatchStatementStorageKeys {
  return {modelStorage: `bill-payment-bank-edi-${jobName}`}
}

function SelectRenderer({data, row}: any) {
  const toggleSelection = data[row.index].toggleSelection;
  const isView = !data[row.index].useEmail;

  return (
    <div style={{width: '100%'}}>
      {!isView ?
        <CheckBoxUncontrolled focusId={`item-chk-${row.index}`} initialValue={data[row.index].select}
              onChange={() => toggleSelection(row.index)} disabled={isView} />
        :
        <CheckBox value={false} disabled />        
      }
    </div>
  );
}

function ActionRenderer({data, row, cell}: any) {
  const previewReport = data[row.index].previewReport;
  const rowData = data[row.index];

  return <BlueTextRenderer cell={cell} tooltip={""} onClick={() => previewReport(rowData)} />
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function BatchStatementImpl(params: BatchStatementViewParams) {
  const {jobName, id} = params;
  const {modelStorage} = getBatchStatementStorageKeys(jobName);
  const appContext = useContext(ApplicationContext);
  const [inStorage, setInStorage, , inStorageRef] = useTypedFastStorage<BatchStatementHd>(modelStorage, null);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const channel = postal.channel('ifms');

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadExisting(): Promise<BatchStatementHd> {
    const currentDate = getDate(now());

    return new BatchStatementHd({
      overDue: actualDate[0],
      invoiceDateTo: currentDate,
      workWith:'E',
      emailSubject: `Statement for $$CUST$$ on ${formatDate(currentDate)}`,
      bccToMe: true,
      bccEmail: "" + (appContext && appContext.user ? appContext.user.email : ""),
      attachInvoices: true,
      detailList: []
    });
  }
  
  async function loadList() {
    const apiResult = await appContext.api.readStatementData(inStorage?.overDue, inStorage?.excludeZeroTerm, inStorage?.invoiceDateFrom, inStorage?.invoiceDateTo);

    if (!apiResult) return;

    if(apiResult.length === 0) {
      await showGlobalOkDialog(<p>There is no line item.</p>);
      setInStorage({detailList: []})
      return;
    }

    const detailList: BatchStatementDetail[] = apiResult as BatchStatementDetail[];

    setInStorage({detailList: detailList});

    // Clear selection if thers is a previous selection.
    for (let i = 0; i < detailList.length; i++) {
      if (detailList[i].useEmail) {
        channel.publish('ifms.chk-uncontrolled', {id: `item-chk-${i}`, value: false});
      }
    }
  }

  async function initialize() {
    const data = await loadExisting();
    setInStorage({...data})
  }

  if (!inStorage) {
    return null;
  }

  return (
    <BatchStatementInitializedImpl
      batchStatement={inStorage!}
      batchStatementRef={inStorageRef}
      setBatchStatement={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
      tbId={id}
      appContext={appContext}
      loadList={loadList}
    />
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function BatchStatementInitializedImpl({
  batchStatement, batchStatementRef, setBatchStatement, onPopupClose,
  jobName, tbId, appContext, loadList
}: BatchStatementLoadedDetailsViewParams) {

  const progressRef = useRef(false);
  const { viewReport } = useReportViewer();
  const channel = postal.channel('ifms');
  const [showGlobalOkDialog] = useGlobalOkDialog();

  useEffect(() => {
    focusTabableByIndex(0);
  }, []);

  function onChange(change: Partial<BatchStatementHd>) {
    setBatchStatement(change);
  }

  function selectAll() {
    if (!batchStatement.detailList || batchStatement.detailList.length <= 0) return;

    for (let i = 0; i < batchStatement.detailList.length; ++i) {
      if (batchStatement.detailList[i].useEmail) {
        batchStatement.detailList[i].select = !isDeselectAll;
        channel.publish('ifms.chk-uncontrolled', {id: `item-chk-${i}`, value: !isDeselectAll});
      }
    }

    onChange({detailList: batchStatement.detailList});
  }

  function toggleSelection(i: number) {
    const _list = _.cloneDeep(batchStatement.detailList);
    if (_list[i].useEmail) {
      _list[i].select = !_list[i].select;
      setBatchStatement({detailList: _list});
    }
  }
  
  function makeReportArgs(row: any, isPreiview: boolean){
    if (!row) return null;

    const title = batchStatement.emailSubject.replace("$$CUST$$", row.billToFName);
    const bccmail = batchStatement.bccToMe ? batchStatement.bccEmail : '';
    const comInfo = (isPreiview ? `|${row.billTo}` : `${row.billTo}|${row.billToSName}|${row.contact}|${row.fax}|${row.email}`);

    const args = new LocalStatementArgs({
      docType: DocumentType.LocalStatement,
      formCaption: title,
      subject: title,
      toCompanyid: comInfo,
      memo: batchStatement.emailBody,
      tbName: "BatchStatement",
      tbId: "0",
      bccEmail: bccmail,

      billto: row.billTo,
      startDate: batchStatement.invoiceDateFrom,
      endDate: batchStatement.invoiceDateTo,
      reportType: "Local Invoice",
      paidType: "OPEN",
      showDetail: batchStatement.attachInvoices,
      dateType: "Invoice",
      sortType: "F_InvoiceDate",
      currency: row.currency,
      advance: "0",
      asOf: batchStatement.invoiceDateFrom == null ? "As of" : "Period",
      pBalShow: true,
      departs: "OI,AI,AO,OO,GE,WR,WS,",
      useMultiCur: false,   // <MARK> Need to change // fmsGlobal.FmsMOption?.accountingOption?.showCurAmountInInv,
      pastDue: batchStatement.overDue,
      excl0Term: batchStatement.excludeZeroTerm
    })

    return args;
  }

  async function previewReport(row: any){
    const args = makeReportArgs(row, true);
    viewReport(args, "", true);
  }

  function showProgressDialog(show: boolean) {
    const el = document.getElementById('statement-progress-dialog');
    if (el) {
      el.style.display = show ? 'block' : 'none';
    }
  }

  function stopProgress() {
    progressRef.current = false;
  }

  function setProgressLabel(current: number, total: number) {
    const el = document.getElementById('statement-progress-counter');
    if (el) {
      el.innerHTML = `${current} / ${total}`;
    }
  }

  function setStatus(index: number, status: string) {
    const _list = _.cloneDeep(batchStatement.detailList);
    _list[index].status = status;
    onChange({detailList: _list});
  }

  async function sendEmail() {
    if (!batchStatement.emailSubject) {
      await showGlobalOkDialog("There is no Email Subject.");
      return;
    }
    if (!batchStatement.emailBody) {
      await showGlobalOkDialog("There is no Email Body.");
      return;
    }

    const actionRowCount = batchStatement.detailList.filter(x => x.select).length;
    let successRowCount = 0;
    if (actionRowCount === 0) return;

    progressRef.current = true;

    showProgressDialog(true);

    let currentIndex = 1;

    // + Loop

    for (let i = 0; i < batchStatement.detailList.length; i++) {
      if (!progressRef.current) {
        showProgressDialog(false);
        break;
      }

      const x = batchStatement.detailList[i];
      if (!x.select) continue;

      setProgressLabel(currentIndex++, actionRowCount);
      setStatus(i, "Working");

      //

      const args = makeReportArgs(x, false);

      const apiResult = await appContext.api.sendEMail(args);
      if (!apiResult || !apiResult.success) {
        setStatus(i, "Error");
      } else {
        setStatus(i, "Done");
        successRowCount++;
      }
    }

    showProgressDialog(false);

    await showGlobalOkDialog(<p>{`${successRowCount} out of ${actionRowCount} selected have been sent.`}</p>);
  }

  const tableOptions: ApplicableTableSettings = {
    tableName: 'batchStatementProcessingTable',
    ...batchStatementProcessingTable,
    data: batchStatement.detailList.map((x, i) => ({...x, i, toggleSelection, previewReport})),
    rowRenderers: [
      {accessor: 'indicator', Cell: NumberedIndicatorRenderer, },
      {accessor: 'billToSName', Cell: TextAlignLeftRenderer, },
      {accessor: 'currency', Cell: TextRenderer, },
      //{accessor: 'maxOverDueDays', Cell: FloatRenderer, },
      {accessor: 'maxOverDueDays', Cell: (props: any) => <FloatRenderer {...props} decimals={0}/>},
      {accessor: 'lastPaidDate', Cell: DateRenderer, },
      {accessor: 'currentAmount', Cell: FloatRenderer, },
      {accessor: 'pastDueAmount', Cell: FloatRenderer, },
      //{accessor: 'pastDueCount', Cell: FloatRenderer, },
      {accessor: 'pastDueCount', Cell: (props: any) => <FloatRenderer {...props} decimals={0}/>},
      {accessor: 'totalAmount', Cell: FloatRenderer, },
      {accessor: 'useEmail', Cell: NullableCheckboxIconRenderer, },
      {accessor: 'select', Cell: SelectRenderer, },
      {accessor: 'status', Cell: TextRenderer, },
      //{accessor: 'action', Cell: BlueTextRenderer, },
      //{accessor: 'action', Cell: ({cell}: any, {props}: any) => <BlueTextRenderer cell={cell} tooltip={""} onClick={() => previewReport(props)} />, },
      {accessor: 'action', Cell: ActionRenderer, },
    ],
    rowProps: (row: any) => {
      if (!batchStatement.detailList[row.index].useEmail) {
          return {
            style: { backgroundColor: 'rgb(225, 225, 225)' }
          };
      }
      switch (batchStatement.detailList[row.index].status) {
        case "Working":
          return {
            style: { backgroundColor: 'rgb(225, 225, 0)' }
          };
        case "Done":
          return {
            style: { backgroundColor: 'rgb(0, 225, 0)' }
          };
        case "Error":
          return {
            style: { backgroundColor: 'rgb(225, 0, 0)' }
          };
      }

      return undefined;
    },    
  };

  const isDeselectAll = batchStatement.detailList.length > 0 && batchStatement.detailList.filter(f => f.useEmail).every(x => x.select);
  const allButtonLabel = isDeselectAll ? 'Cancel All' : 'Select All';

  return (
    <div className='report-popup'>
      <div className='report-row' style={{marginLeft: 30, display: 'flex'}}>
        <div style={{width: 180}}>
          <InlineInputControlRow label="Actual Date" mode='w80'>
            <Select name='batch-statement-actualDate' value={batchStatement.overDue??actualDate[0]} 
            onChange={e => {
              const v = e.target.value;
              onChange({overDue: v??actualDate[0]});
            }}
            items={actualDate.map(x=>({label: x, value: x}))}  />
          </InlineInputControlRow>
        </div>
        <div style={{marginLeft: 10, width: 70}}>
          <CheckBox id='batch-statement-excludeZeroTerm-chk' focusId='batch-statement-excludeZeroTerm' value={batchStatement.excludeZeroTerm}
              onChange={excludeZeroTerm => onChange({excludeZeroTerm})}
              label="Exclude zero term"/>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <PrimaryButton name='batch-statement-select-button' focusable={true} size='small' onClick={selectAll}>{allButtonLabel}</PrimaryButton>
          <GreenButton name='batch-statement-list-button' focusable={true} style={{ width: '130px', marginLeft: '15px' }} size='small' onClick={() => loadList()}>LIST</GreenButton>
        </div>
      </div>
      <div className='report-row mt-1'>
        <SmartTable {...tableOptions} />
      </div>
      <div className='report-row' style={{marginTop: 8, display: 'flex'}}>
        <div style={{width: 250}}>
          <Label label='** E-Mail Body Macro **' bold />
          <Label label='    Customer Name: $$CUST$$'  />
        </div>
        <div style={{width: 470}}>
          <InlineInputControlRow label="Email Subject" mode='w80' smallMargin>
            <TextBox name='batch-statement-emailSubject' value={batchStatement.emailSubject}
                     onChange={e => onChange({emailSubject: e.target.value})}/>
          </InlineInputControlRow>
          <InlineInputControlRow label="Email Body" mode='w80' smallMargin>
            <MultilineTextBox name='batch-statement-emailBody' value={batchStatement.emailBody}
                     onChange={e => onChange({emailBody: e.target.value})}/>
          </InlineInputControlRow>
        </div>
        <div style={{width: 250, marginLeft: 50}}>
          <div>
            <CheckBox id='batch-statement-bccToMe-chk' focusId='batch-statement-bccToMe' label='BCC to me' value={batchStatement.bccToMe} 
              onChange={checked => onChange({bccToMe: checked})} /></div>
          <div style={{marginLeft: 0, marginTop: 10}}>
            <InlineInputControlRow label="* Email" mode='w50' smallMargin>
              <TextBox name='batch-statement-bccEmail' value={batchStatement.bccEmail}
                       onChange={e => onChange({bccEmail: e.target.value})}/>
            </InlineInputControlRow>
          </div>
          <div><CheckBox id='batch-statement-attachInvoices-chk' focusId='batch-statement-attachInvoices' label='Attach Invoice(s)' value={batchStatement.attachInvoices} onChange={checked => onChange({attachInvoices: checked})} /></div>
        </div>
      </div>
      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton name='batch-statement-sendemail-button' focusable={true} size="regular" disabled={!batchStatement.detailList.some(x=>x.select)}  onClick={() => sendEmail()}>SEND EMAIL</SecondaryButton>
        <SecondaryButton
        focusable={true}
          size="regular"
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>

      <Portal node={document.body}>
        <div id="statement-progress-dialog" style={ProgressDialogStyle}>
          <div style={ProgressDialogContainerStyle}>
            <div style={{display: 'flex'}}>
              <div style={{marginRight: '20px'}}>
                <DangerButton name='batch-statement-stop-button' focusable={true} onClick={stopProgress}>STOP</DangerButton>
              </div>
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div><strong>Please wait while processing...</strong></div>
                <div id="statement-progress-counter"></div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const BatchStatementDetails = ({ onPopupClose, jobName, selectedElementId }
    : { onPopupClose?: () => void, jobName: JobNames, selectedElementId: number }) => {

  return (
    <BatchStatementImpl id={selectedElementId.toString()} jobName={jobName} onPopupClose={onPopupClose} key={`batchStatement-details`}/>
  );
};
export default BatchStatementDetails;
